import { useEffect, useState, type ReactNode } from "react";
import { Icon } from "../../components/Icon";
import { Btn, Card, PageHead, Seg, Toggle } from "../../components/kit";
import { clearHistory, exportData, logout, useMe } from "../../lib/api";
import { useSettings, saveSettings } from "../../lib/hooks";
import { applyTheme, type ThemePref } from "../../lib/theme";
import { syncAnalyticsPref } from "../../lib/analytics";

function Row({ icon, t, d, children }: { icon: string; t: string; d?: string; children?: ReactNode }) {
  return (
    <div className="set-row">
      <span className="set-ic"><Icon name={icon} size={17} /></span>
      <div className="set-body">
        <div className="set-t">{t}</div>
        {d && <div className="set-d">{d}</div>}
      </div>
      {children}
    </div>
  );
}

const DEFAULTS = { retain: true, telemetry: false, lessonAlerts: true, limitAlerts: true, weekly: false, theme: "System" };

export function SettingsPage() {
  const stored = useSettings();
  const me = useMe();
  const [s, setS] = useState(DEFAULTS);

  // Merge stored settings over defaults once they load, and sync the theme to
  // the server value (covers a change made on another device).
  useEffect(() => {
    if (stored) {
      setS((p) => ({ ...p, ...stored }));
      if (typeof stored.theme === "string") applyTheme(stored.theme as ThemePref);
      if (typeof stored.telemetry === "boolean") syncAnalyticsPref(stored.telemetry);
    }
  }, [stored]);

  const set = (k: keyof typeof s, v: boolean | string) => {
    const next = { ...s, [k]: v };
    setS(next);
    if (k === "theme") applyTheme(v as ThemePref); // live preview + cache instantly
    if (k === "telemetry") syncAnalyticsPref(v as boolean); // opt in/out immediately
    if (stored) saveSettings(next).catch(() => {}); // persist when signed in
  };
  return (
    <div className="page">
      <PageHead title="Settings" sub="Account-level preferences. Device controls — permissions, hotkeys — live in the desktop app." />

      <div className="two-col">
        <div className="stack-lg">
          <Card title="Privacy & data" icon="shield" pad={false}>
            <div className="set-list">
              <Row icon="database" t="Store conversation history" d="Keep past asks searchable in History"><Toggle on={s.retain} onChange={(v) => set("retain", v)} /></Row>
              <Row icon="line-chart" t="Share anonymous usage" d="Help improve Orbit with telemetry"><Toggle on={s.telemetry} onChange={(v) => set("telemetry", v)} /></Row>
              <Row icon="trash-2" t="Clear all history" d="Permanently delete stored conversations"><Btn variant="ghost" size="sm" onClick={() => { if (window.confirm("Permanently delete all stored conversations?")) clearHistory().catch(() => {}); }}>Clear</Btn></Row>
              <Row icon="download" t="Export your data" d="Download everything Orbit stores"><Btn variant="ghost" size="sm" icon="download" onClick={() => exportData().catch(() => window.alert("Couldn’t export — try again."))}>Export</Btn></Row>
            </div>
          </Card>

          <Card title="Appearance" icon="palette" pad={false}>
            <div className="set-list">
              <Row icon="sun-moon" t="Theme"><Seg options={["System", "Light", "Dark"]} value={s.theme} onChange={(v) => set("theme", v)} /></Row>
            </div>
          </Card>
        </div>

        <div className="stack-lg">
          <Card title="Notifications" icon="bell" pad={false}>
            <div className="set-list">
              <Row icon="graduation-cap" t="Lesson reminders" d="Nudge me to keep my streak"><Toggle on={s.lessonAlerts} onChange={(v) => set("lessonAlerts", v)} /></Row>
              <Row icon="zap" t="Usage & limit alerts" d="Warn me before I run out of credits"><Toggle on={s.limitAlerts} onChange={(v) => set("limitAlerts", v)} /></Row>
              <Row icon="mail" t="Weekly summary email" d="Progress recap every Monday"><Toggle on={s.weekly} onChange={(v) => set("weekly", v)} /></Row>
            </div>
          </Card>

          <Card title="Account" icon="user-round" pad={false}>
            <div className="set-list">
              <Row icon="at-sign" t="Email" d={me?.email}><Btn variant="ghost" size="sm" onClick={() => window.alert("Email is managed through your WorkOS sign-in.")}>Change</Btn></Row>
              <Row icon="log-out" t="Sign out" d="Sign out of the web app"><Btn variant="ghost" size="sm" onClick={() => { logout(); location.href = "/"; }}>Sign out</Btn></Row>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
